import React, { useState, useEffect } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import TabNavigation from "../../../../../components/common/tabNavigation";
import LoadingOverlay from "../../../../../components/common/LoadingOverlay";
import {
  getMissionHistoryAPI,
  getKeywordNameAPI,
  getCoupangProductInfoAPI,
  patchAdExtensionAPI,
} from "../../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../../utils/helpers";
import { useAuth } from "../../../../../hooks/useAuth";
import "../AdDetailModal.css";

const CoupangAdDetail = ({
  isOpen,
  onClose,
  adData,
  keywordData,
  priceGradeData,
  getUserDetailAPI,
  onSave,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("details");
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryIds, setExpandedHistoryIds] = useState(new Set());
  const [keywordMap, setKeywordMap] = useState({});
  const [refreshLoading, setRefreshLoading] = useState(false);

  useEffect(() => {
    if (activeTab === "history" && isOpen && adData?.ad_id) {
      loadHistoryData();
    }
  }, [activeTab, isOpen, adData?.ad_id]);

  const loadHistoryData = async () => {
    if (!adData?.ad_id) return;
    try {
      setHistoryLoading(true);
      const response = await getMissionHistoryAPI(adData.ad_id);
      const historyList = response.data?.data || [];
      setHistoryData(historyList);

      const keywordIds = new Set();
      historyList.forEach((history) => {
        if (history.beforeSnapshot) {
          if (history.beforeSnapshot.mainKeywordId)
            keywordIds.add(history.beforeSnapshot.mainKeywordId);
          (history.beforeSnapshot.subKeywordIds || []).forEach((id) =>
            keywordIds.add(id)
          );
        }
        if (history.afterSnapshot) {
          if (history.afterSnapshot.mainKeywordId)
            keywordIds.add(history.afterSnapshot.mainKeywordId);
          (history.afterSnapshot.subKeywordIds || []).forEach((id) =>
            keywordIds.add(id)
          );
        }
      });

      const map = {};
      if (keywordData?.length) {
        keywordData.forEach((k) => {
          if (k.keyword_id) map[k.keyword_id] = k.name;
        });
      }
      const missingIds = Array.from(keywordIds).filter((id) => !map[id]);
      if (missingIds.length > 0) {
        try {
          const keywordResponse = await getKeywordNameAPI(
            null,
            null,
            missingIds,
            1,
            2000
          );
          (keywordResponse.data?.data || []).forEach((k) => {
            if (k.keyword_id && !map[k.keyword_id])
              map[k.keyword_id] = k.name;
          });
        } catch (err) {
          console.error("키워드 이름 로드 실패:", err);
        }
      }
      setKeywordMap(map);
    } catch (error) {
      console.error("내역 데이터 로드 실패:", error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistoryExpanded = (historyId) => {
    setExpandedHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(historyId)) next.delete(historyId);
      else next.add(historyId);
      return next;
    });
  };

  const handleRefreshAdData = async () => {
    if (!adData?.ad_id || !adData?.productUrl) {
      alert("광고 정보 또는 상품 URL이 없습니다.");
      return;
    }
    const productUrl = (adData.productUrl || "").trim();

    try {
      setRefreshLoading(true);

      const infoRes = await getCoupangProductInfoAPI(productUrl);
      if (!infoRes?.data?.success || !infoRes?.data?.data) {
        alert("쿠팡 상품 정보를 가져오지 못했습니다.");
        setRefreshLoading(false);
        return;
      }
      const data = infoRes.data.data;

      let memoData = {};
      try {
        if (adData.memo) memoData = JSON.parse(adData.memo);
      } catch (e) {}
      const updatedMemo = {
        ...memoData,
        originPrice: data.price ?? memoData.originPrice,
        unitPrice: memoData.unitPrice,
        originProductName: memoData.originProductName,
        adTag: memoData.adTag ?? "",
      };

      const extensionData = {
        ad_id: adData.ad_id,
        storeName: data.storeName ?? adData.storeName,
        missionName: data.productName ?? adData.missionName,
        imageUrl: data.imageUrl ?? adData.imageUrl,
        productUrl: data.originalUrl ?? adData.productUrl,
        memo: JSON.stringify(updatedMemo),
      };
      if (Array.isArray(data.reviews) && data.reviews.length > 0) {
        extensionData.contents = data.reviews;
      }

      await patchAdExtensionAPI(extensionData);
      alert("데이터가 성공적으로 업데이트되었습니다.");
      if (onSave) await onSave();
    } catch (error) {
      console.error("다시 받아오기 실패:", error);
      alert(
        error?.response?.data?.msg ||
          "다시 받아오기 중 오류가 발생했습니다."
      );
    } finally {
      setRefreshLoading(false);
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case "STATUS_CHANGED":
        return "상태 변경";
      case "UPDATED":
        return "업데이트";
      case "CREATED":
        return "생성";
      default:
        return action;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  if (!isOpen || !adData) return null;

  const getStatusText = (status) => {
    const map = {
      APPROVED: "진행중",
      PAUSED: "일시정지",
      PENDING: "승인대기중",
      REJECTED: "거절",
      REFUND: "환불",
      DONE: "종료",
    };
    return map[status] || status;
  };

  const getStatusClass = (status) => {
    const map = {
      APPROVED: "status-progress",
      PENDING: "status-pending",
      PAUSED: "status-paused",
      REJECTED: "status-cancelled",
      REFUND: "status-refund",
      DONE: "status-ended",
    };
    return map[status] || "";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].replace(/-/g, ".");
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  let memoData = {};
  try {
    if (adData.memo) memoData = JSON.parse(adData.memo);
  } catch (e) {}

  const mainKeywordName =
    keywordData?.find((k) => k.type === "MAIN")?.name || "-";
  const mainKeywordRank = adData.mainKeywordRank ?? "-";

  return (
    <>
      <LoadingOverlay
        isVisible={refreshLoading}
        message="다시 받아오는 중..."
      />
      <div className="ad-detail-modal-overlay" onClick={onClose}>
        <div className="ad-detail-modal coupang-ad-detail" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: "15px", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
                <div className="thumbnail-image">
                  <img
                    src={adData.imageUrl || "/logo192.png"}
                    alt={adData.missionName || "상품 이미지"}
                    className="product-thumbnail"
                    onError={(e) => {
                      e.target.src = "/logo192.png";
                    }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "5px", flex: 1 }}>
                  <div className="store-name">{adData.storeName || "-"}</div>
                  <div className="mission-name">{adData.missionName || "-"}</div>
                </div>
              </div>
              <div className="remaining-days">
                <RemainingDaysBadge
                  startDate={adData.startDate}
                  endDate={adData.endDate}
                  status={adData.status}
                />
              </div>
              <div className={`status-badge ${getStatusClass(adData.status)}`}>
                {getStatusText(adData.status)}
              </div>
              {user?.role !== "CHECKER" &&
                (adData.status === "PENDING" ||
                  adData.status === "PAUSED" ||
                  adData.status === "APPROVED") && (
                  <button
                    className="ad-refresh-btn"
                    onClick={handleRefreshAdData}
                    disabled={refreshLoading}
                    title="다시 받아오기"
                  >
                    {refreshLoading ? "받아오는 중..." : "다시 받아오기"}
                  </button>
                )}
            </div>
          </div>

          <TabNavigation
            tabs={[
              { id: "details", label: "광고 상세 정보" },
              ...(user?.role !== "CHECKER" ? [{ id: "history", label: "내역" }] : []),
            ]}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />

          <div className="modal-content">
            {activeTab === "details" && (
              <div className="form-grid">
                <div className="form-row">
                  <label>카테고리</label>
                  <div className="form-value">쿠팡</div>
                </div>
                <div className="form-row">
                  <label>스토어명</label>
                  <div className="form-value">{adData.storeName || "-"}</div>
                </div>
                <div className="form-row">
                  <label>광고명</label>
                  <div className="form-value">{adData.missionName || "-"}</div>
                </div>
                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>미션 방법</label>
                    <div className="form-value">{adData.productName || "퀴즈"}</div>
                  </div>
                )}
                <div className="form-row">
                  <label>상품 URL</label>
                  <div
                    className="form-value"
                    style={{
                      wordBreak: "break-all",
                      maxHeight: "3em",
                      overflow: "auto",
                    }}
                    title={adData.productUrl}
                  >
                    {adData.productUrl ? (
                      <a
                        href={adData.productUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#3b82f6" }}
                      >
                        {adData.productUrl}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                {user?.role !== "CHECKER" && (
                  <>
                    <div className="form-row">
                      <label>메인 키워드</label>
                      <div className="form-value">{mainKeywordName}</div>
                    </div>
                    <div className="form-row">
                      <label>메인 키워드 순위</label>
                      <div className="form-value">
                        {mainKeywordRank !== "-" ? `${mainKeywordRank}위` : "-"}
                      </div>
                    </div>
                    {memoData.originPrice !== undefined && (
                      <div className="form-row">
                        <label>원가</label>
                        <div className="form-value">{memoData.originPrice}</div>
                      </div>
                    )}
                  </>
                )}
                <div className="form-row">
                  <label>광고 시작일</label>
                  <div className="form-value">{formatDate(adData.startDate)}</div>
                </div>
                <div className="form-row">
                  <label>광고 종료일</label>
                  <div className="form-value">{formatDate(adData.endDate)}</div>
                </div>
                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>1회 단가</label>
                    <div className="form-value" title={`${formatNumber(Math.round(adData.price / adData.totalQuantity))}원`}>
                      {formatNumber(Math.round(adData.price / adData.totalQuantity))}원
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <label>총 수량</label>
                  <div className="form-value" title={formatNumber(adData.totalQuantity)}>
                    {formatNumber(adData.totalQuantity)}
                  </div>
                </div>
                <div className="form-row">
                  <label>일일 수량</label>
                  <div className="form-value" title={formatNumber(adData.dailyQuantity)}>
                    {formatNumber(adData.dailyQuantity)}
                  </div>
                </div>
                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>결제 금액</label>
                    <div className="form-value" title={`${formatNumber(adData.price || 0)}원`}>
                      {formatNumber(adData.price || 0)}원
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <label>마감 예정일</label>
                  <div className="form-value" title={formatDate(adData.endDate)}>
                    {formatDate(adData.endDate)}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "history" && (
              <div className="ad-history-section">
                <h4>변경 내역</h4>
                <div className="ad-history-content">
                  {historyLoading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <p>내역을 불러오는 중...</p>
                    </div>
                  ) : historyData.length === 0 ? (
                    <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>
                      변경 내역 데이터가 없습니다.
                    </p>
                  ) : (
                    <div className="ad-history-timeline">
                      {historyData.map((history) => {
                        const isExpanded = expandedHistoryIds.has(history._id);
                        const changedAt = history.changedAt
                          ? convertUTCToKoreaTime(history.changedAt)
                          : null;
                        const changedAtStr = changedAt
                          ? changedAt.toLocaleString("ko-KR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "";
                        return (
                          <div key={history._id} className="ad-history-entry">
                            <div
                              className="ad-history-entry-header"
                              onClick={() => toggleHistoryExpanded(history._id)}
                            >
                              <div className="ad-history-entry-header-left">
                                <span className="ad-history-entry-action">
                                  {getActionLabel(history.action)}
                                </span>
                                <span className="ad-history-entry-reason">
                                  {history.reason || "-"}
                                </span>
                                {history.admin_memo && (
                                  <span className="ad-history-entry-memo">
                                    ({history.admin_memo})
                                  </span>
                                )}
                              </div>
                              <div className="ad-history-entry-header-right">
                                <span className="ad-history-entry-date">{changedAtStr}</span>
                                <span className="ad-history-entry-toggle">
                                  {isExpanded ? "▼" : "▶"}
                                </span>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="ad-history-entry-details">
                                <div className="ad-history-entry-changed-fields">
                                  <strong>변경된 필드:</strong>{" "}
                                  {history.changedFields?.join(", ") || "-"}
                                </div>
                                <div className="ad-history-entry-snapshots">
                                  <div className="snapshot-section">
                                    <h5 className="snapshot-title before">변경 전</h5>
                                    <div className="snapshot-content">
                                      {history.beforeSnapshot ? (
                                        <HistorySnapshot
                                          snapshot={history.beforeSnapshot}
                                          changedFields={history.changedFields || []}
                                          keywordMap={keywordMap}
                                        />
                                      ) : (
                                        <p>데이터 없음</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="snapshot-section">
                                    <h5 className="snapshot-title after">변경 후</h5>
                                    <div className="snapshot-content">
                                      {history.afterSnapshot ? (
                                        <HistorySnapshot
                                          snapshot={history.afterSnapshot}
                                          changedFields={history.changedFields || []}
                                          keywordMap={keywordMap}
                                        />
                                      ) : (
                                        <p>데이터 없음</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="ad-detail-close-btn" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// 내역 스냅샷 (NormalAdDetail과 동일 구조)
const HistorySnapshot = ({ snapshot, changedFields = [], keywordMap = {} }) => {
  const getStatusLabel = (status) => {
    const map = {
      PENDING: "승인대기중",
      APPROVED: "진행중",
      PAUSED: "일시정지",
      REJECTED: "거절",
      DONE: "종료",
      REFUND: "환불",
    };
    return map[status] || status;
  };
  const isChanged = (fieldName) => changedFields.includes(fieldName);
  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "예" : "아니오";
    if (Array.isArray(value)) return value.join(", ");
    return value;
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("ko-KR");
    } catch (e) {
      return dateString;
    }
  };
  let memoData = {};
  try {
    if (snapshot.memo) memoData = JSON.parse(snapshot.memo);
  } catch (e) {}
  const renderField = (label, fieldName, value, formatter = null, forceChanged = false) => {
    const formatted = formatter ? formatter(value) : formatValue(value);
    const changed = forceChanged || isChanged(fieldName);
    return (
      <div className="snapshot-row" key={fieldName + label}>
        <span className="snapshot-label">{label}:</span>
        <span className={`snapshot-value ${changed ? "changed-field" : ""}`}>{formatted}</span>
      </div>
    );
  };
  return (
    <div className="snapshot-data">
      {renderField("서비스명", "serviceName", snapshot.serviceName)}
      {renderField("광고명", "missionName", snapshot.missionName)}
      {renderField("상품명", "productName", snapshot.productName)}
      {renderField("스토어명", "storeName", snapshot.storeName)}
      {renderField("상품 URL", "productUrl", snapshot.productUrl)}
      {renderField("상태", "status", getStatusLabel(snapshot.status))}
      {renderField(
        "가격",
        "price",
        snapshot.price ? `${snapshot.price.toLocaleString()}원` : "-"
      )}
      {renderField("총 수량", "totalQuantity", snapshot.totalQuantity)}
      {renderField("일일 수량", "dailyQuantity", snapshot.dailyQuantity)}
      {renderField("시작일", "startDate", formatDate(snapshot.startDate))}
      {renderField("종료일", "endDate", formatDate(snapshot.endDate))}
      {renderField(
        "메인 키워드",
        "mainKeywordId",
        snapshot.mainKeywordId
          ? `${keywordMap[snapshot.mainKeywordId] || "알 수 없음"} (ID: ${snapshot.mainKeywordId})`
          : "-"
      )}
      {renderField(
        "서브 키워드",
        "subKeywordIds",
        snapshot.subKeywordIds?.length
          ? snapshot.subKeywordIds
              .map((id) => `${keywordMap[id] || "알 수 없음"} (ID: ${id})`)
              .join(", ")
          : "-"
      )}
      {memoData.originPrice !== undefined &&
        renderField("원가", "memo", memoData.originPrice, null, isChanged("memo"))}
      {memoData.unitPrice !== undefined &&
        renderField("적용단가", "memo", memoData.unitPrice, null, isChanged("memo"))}
    </div>
  );
};

export default CoupangAdDetail;
